import React, { useEffect, useState } from "react";
import Dropzone, { defaultClassNames, IDropzoneProps } from "react-dropzone-uploader";

import { Button, LoadingOverlay } from "@mantine/core";
import { ErrorResponse } from "App";
import AWS from "aws-sdk";
import {
  ButtonWrapper,
  DropZoneStyle,
  DropZoneText,
  DropzoneWrapper,
  DropZoneWrapper,
  InnerWrapper,
  StyleButtonWrapper,
  StyleDescription,
  StyleDropzone,
  StyleDropzoneWrapper,
  StyleFooter,
  StyleSubTitle,
  StyleValidationMessagePlaceHolder
} from "modules/imports/components/Import.styles";
import { TransactionFileDataInterface } from "modules/imports/components/ImportsTypes";
import moment from "moment";
import Loader from "ui/feedback/Loader";
import { FileUploadIcon } from "ui/icons";
import ErrorModal from "ui/overlays/ErrorModal";
import Modal from "ui/overlays/Modal";
import { ModalTitle } from "ui/styles";
import { getAccountName } from "utils/common.utils";

import { useUpdateAnalysisImportMutation } from "../hooks";
import { ConfirmationUploadModal } from "./ConfirmationUploadModal";

const REGION = process.env.REACT_APP_REGION;
const S3_BUCKET = process.env.REACT_APP_BUCKET_NAME;

AWS.config.update({
  accessKeyId: process.env.REACT_APP_ACCESS,
  secretAccessKey: process.env.REACT_APP_SECRET
});

const myBucket = new AWS.S3({
  params: { Bucket: S3_BUCKET },
  region: REGION
});

type UploadAnalysisFileProps = {
  handleClose: () => void;
  showModal: boolean;
  transactionId: string | null;
};

export const UploadAnalysisFile: React.FC<UploadAnalysisFileProps> = ({ showModal, handleClose, transactionId }) => {
  const [process, setProcess] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [analysisFile, setAnalysisFile] = useState<TransactionFileDataInterface>();
  const [errorModal, setErrorModal] = useState<boolean>(false); // state controller for error Modal
  const [message, setMessage] = useState<string>(""); // message to display on success or error
  const updateAnalysisImportMutation = useUpdateAnalysisImportMutation();

  const Preview = (): JSX.Element => {
    return (
      <DropzoneWrapper key="Dropzone">
        <FileUploadIcon style={{ marginLeft: "auto", marginRight: "auto", marginBottom: "13px" }} />
        <DropZoneText>
          Drag and drop file(s) here or <span style={{ fontWeight: 700 }}>Browse Files</span>
          <div style={{ alignSelf: "flex-start", margin: "20px 3%", fontFamily: "Inter" }}>{analysisFile?.name}</div>
        </DropZoneText>
      </DropzoneWrapper>
    );
  };

  useEffect(() => {
    if (updateAnalysisImportMutation.isSuccess) {
      setAnalysisFile({});
      setShowConfirmModal(true);
      setProcess(false);
    }
  }, [updateAnalysisImportMutation.isSuccess]);

  useEffect(() => {
    if (updateAnalysisImportMutation.isError === true) {
      setAnalysisFile({});
      const errorMessage =
        (updateAnalysisImportMutation.error as ErrorResponse).response.data.errorMessage ?? "Something Went Wrong.";
      setMessage(errorMessage);
      setErrorModal(true);
      setProcess(false);
    }
  }, [updateAnalysisImportMutation.isError]);

  const handleUploadFile = () => {
    if (analysisFile?.name === undefined || transactionId === null) {
      return false;
    }

    if (!S3_BUCKET) return;

    setProcess(true);
    const fileName = getAccountName().toLowerCase() + moment.now() + ".csv";
    const params: AWS.S3.PutObjectRequest = {
      ACL: "private",
      Body: analysisFile,
      ContentType: analysisFile.type,
      Bucket: S3_BUCKET,
      Key: fileName
    };
    myBucket
      .putObject(params)
      .promise()
      .then(() => {
        updateAnalysisImportMutation.mutate({
          analysisFileLocation: fileName,
          id: transactionId
        });
      })
      .catch(() => {
        setProcess(false);
        setMessage("Upload failed try again.");
        setErrorModal(true);
      });
  };

  const handleChangeStatus: IDropzoneProps["onChangeStatus"] = ({ file }, status) => {
    if (["getting_upload_params", "headers_received", "preparing", "uploading"].includes(status)) {
      setProcess(true);
    } else {
      setProcess(false);
    }
    if (status === "rejected_file_type") {
      setMessage("Accepted File Types: .csv only.");
      setErrorModal(true);
    }
    if (status === "exception_upload") {
      setAnalysisFile({});
      setMessage("Upload failed try again.");
      setErrorModal(true);
    }
    if (status === "done") {
      setAnalysisFile(file);
    }
  };

  const handleResetDropzone = () => {
    setAnalysisFile({});
    setProcess(false);
    handleClose();
  };

  const handleSubmit: IDropzoneProps["onSubmit"] = (_, allFiles) => {
    setAnalysisFile({});
    allFiles.forEach(f => f.remove());
  };

  return (
    <React.Fragment>
      <Modal opened={showModal} handleClose={handleClose} withCloseButton>
        <ModalTitle>Add Analysis File</ModalTitle>
        <StyleSubTitle>Upload the Analysis File</StyleSubTitle>
        <StyleDescription>
          Upload a new Analysis File with transaction data.
          {/* See the FAQ for{" "}<StyleDescTextUnderLine>assistance transaction file formats.</StyleDescTextUnderLine> */}
        </StyleDescription>
        <LoadingOverlay
          visible={process || updateAnalysisImportMutation.isLoading}
          overlayBlur={2}
          loader={<Loader />}
        />
        <StyleDropzoneWrapper>
          <StyleDropzone>
            <DropZoneStyle>
              <DropZoneWrapper>
                <InnerWrapper>
                  <Dropzone
                    maxFiles={1}
                    multiple={false}
                    onChangeStatus={handleChangeStatus}
                    onSubmit={handleSubmit}
                    submitButtonContent={"Cancel"}
                    accept=".csv"
                    disabled={files =>
                      files.some(f => ["getting_upload_params", "preparing", "uploading"].includes(f.meta.status))
                    }
                    classNames={{ inputLabelWithFiles: defaultClassNames.inputLabel }}
                    inputContent={
                      <DropzoneWrapper key="Dropzone">
                        <FileUploadIcon style={{ marginLeft: "auto", marginRight: "auto", marginBottom: "13px" }} />
                        <DropZoneText>
                          Drag and drop file(s) here or <span style={{ fontWeight: 700 }}>Browse Files</span>
                        </DropZoneText>
                      </DropzoneWrapper>
                    }
                    PreviewComponent={Preview}
                  />
                </InnerWrapper>
              </DropZoneWrapper>
            </DropZoneStyle>
          </StyleDropzone>
          <StyleValidationMessagePlaceHolder>Accepted File Type: .csv only</StyleValidationMessagePlaceHolder>
        </StyleDropzoneWrapper>
        <StyleFooter>
          <ButtonWrapper>
            <Button
              disabled={process || updateAnalysisImportMutation.isLoading}
              variant="default"
              onClick={() => handleResetDropzone()}
            >
              Cancel
            </Button>
          </ButtonWrapper>
          <StyleButtonWrapper>
            <ButtonWrapper>
              <Button
                disabled={process || analysisFile?.name === undefined}
                onClick={() => {
                  handleUploadFile();
                }}
              >
                Upload
              </Button>
            </ButtonWrapper>
          </StyleButtonWrapper>
        </StyleFooter>
      </Modal>
      <ConfirmationUploadModal
        openModal={showConfirmModal}
        setOpenModal={setShowConfirmModal}
        handleClose={handleClose}
        title="Your Analysis File has been submitted!"
        description="You may check the status of this import on the Imports page."
        addMoreBtnText="Add More Data Files"
        type="analysis"
      />
      <ErrorModal
        openModal={errorModal}
        setOpenModal={setErrorModal}
        message={message}
        onClose={() => setErrorModal(false)}
      />
    </React.Fragment>
  );
};
